/**
 * React Query hooks for stats API.
 */

import { useQuery } from '@tanstack/react-query'
import { statsApi } from '../api/stats-api'
import type {
  StatsOverviewResponse,
  WeeklyTrendsResponse,
  DomainStatsResponse,
  TenantListResponse,
  OrganizationListResponse,
  Scope
} from '../types'

/**
 * Hook to fetch stats overview with hierarchical breakdown
 */
export function useStatsOverview(params: {
  scope: Scope
  tenantId: string | null
  organizationId: string | null
  start: string
  end: string
}) {
  // Check if required parameters are present based on scope
  const isEnabled =
    params.scope === 'system' ? true :
    params.scope === 'tenant' ? !!params.tenantId :
    params.scope === 'organization' ? !!(params.tenantId && params.organizationId) :
    false

  return useQuery<StatsOverviewResponse>({
    queryKey: ['stats', 'overview', params],
    queryFn: () => statsApi.getOverview(params),
    enabled: isEnabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Hook to fetch weekly trends
 */
export function useWeeklyTrends(params: {
  weeks: number
  scope: Scope
  tenantId: string | null
  organizationId: string | null
  domain?: string
}) {
  // Check if required parameters are present based on scope
  const isEnabled =
    params.scope === 'system' ? true :
    params.scope === 'tenant' ? !!params.tenantId :
    params.scope === 'organization' ? !!(params.tenantId && params.organizationId) :
    false

  return useQuery<WeeklyTrendsResponse>({
    queryKey: ['stats', 'weekly-trends', params],
    queryFn: () => statsApi.getWeeklyTrends(params),
    enabled: isEnabled,
    staleTime: 1000 * 60 * 5,
  })
}

/**
 * Hook to fetch domain statistics
 */
export function useDomainStats(params: {
  page: number
  limit: number
  sort_by: string
  scope: Scope
  tenantId: string | null
  organizationId: string | null
  start?: string
  end?: string
}) {
  // Check if required parameters are present based on scope
  const isEnabled =
    params.scope === 'system' ? true :
    params.scope === 'tenant' ? !!params.tenantId :
    params.scope === 'organization' ? !!(params.tenantId && params.organizationId) :
    false

  return useQuery<DomainStatsResponse>({
    queryKey: ['stats', 'domains', params],
    queryFn: () => statsApi.getDomainStats(params),
    enabled: isEnabled,
    staleTime: 1000 * 60 * 5,
  })
}

/**
 * Hook to fetch all tenants
 */
export function useTenants() {
  return useQuery<TenantListResponse>({
    queryKey: ['stats', 'tenants'],
    queryFn: () => statsApi.getTenants(),
    staleTime: 1000 * 60 * 10, // Tenants change less frequently
  })
}

/**
 * Hook to fetch organizations (optionally filtered by tenant)
 */
export function useOrganizations(tenantId: string | null) {
  return useQuery<OrganizationListResponse>({
    queryKey: ['stats', 'organizations', tenantId],
    queryFn: () => statsApi.getOrganizations({ tenantId }),
    enabled: !!tenantId,
    staleTime: 1000 * 60 * 10,
  })
}
